// node_modules
import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMobile } from "../../../hooks/useMobile";

// redux
import {
  useLazyGetDiningOptionListQuery,
  useUpdateDiningOptionMutation,
  useDeleteDiningOptionMutation,
  useSaveDiningOptionsOrderMutation,
} from "../../../redux/services/diningOption";
import {
  foodStationApi,
  useGetStationListQuery,
} from "../../../redux/services/foodStation";
import { setLoading } from "../../../redux/slices/app.slice";

// primereact components
import { Button } from "primereact/button";
import { OrderList } from "primereact/orderlist";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";

// context
import { ToastContext } from "../../../App";

// utils
import getSubdomain from "../../../utils/getSubdomain";

// styles
import * as styles from "./DiningOptions.module.css";

const DiningOptions = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile } = useMobile(769);

  const [
    getDiningOptions,
    {
      data: options,
      isSuccess: isGetDiningOptionSuccess,
      isError: isGetDiningOptionsError,
    },
  ] = useLazyGetDiningOptionListQuery();
  const [
    updateDiningOption,
    {
      isSuccess: isUpdateDiningOptionSuccess,
      isError: isUpdateDiningOptionError,
      error: updateDiningOptionError,
    },
  ] = useUpdateDiningOptionMutation();
  const [
    deleteDiningOption,
    {
      isSuccess: isDeleteDiningOptionSuccess,
      isError: isDeleteDiningOptionError,
      error: deleteDiningOptionError,
    },
  ] = useDeleteDiningOptionMutation();
  const [
    saveDiningOptionOrder,
    {
      isLoading: isSaveDiningOptionOrderLoading,
      isError: isSaveDiningOptionOrderError,
      error: saveDiningOptionOrderError,
    },
  ] = useSaveDiningOptionsOrderMutation();
  const {
    data: stations,
    isSuccess: isGetStationsSuccess,
    isError: isGetStationsError,
  } = useGetStationListQuery(getSubdomain());

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [id, setId] = useState(null);
  const [name, setName] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);
  const showToast = useContext(ToastContext);

  const goCreate = () => {
    if (!selectedStation) return;
    navigate(`create?station=${selectedStation.id}`);
  };
  const goEdit = (rowData) =>
    navigate(`${rowData.id}/edit?station=${selectedStation.id}`);

  useEffect(() => {
    if (isGetStationsSuccess || isGetStationsError) {
      dispatch(setLoading(false));
    }
  }, [isGetStationsSuccess, isGetStationsError]);

  useEffect(() => {
    if (selectedStation) {
      getDiningOptions({
        id: selectedStation.id,
        subDomain: getSubdomain(),
      });
    }
  }, [selectedStation]);

  useEffect(() => {
    if (isUpdateDiningOptionSuccess) {
      showToast("success", "Dining option", "Dining option updated!");
      dispatch(
        foodStationApi.util.invalidateTags([
          { type: "FoodStations", id: "LIST" },
        ]),
      );
    }
  }, [isUpdateDiningOptionSuccess]);

  useEffect(() => {
    if (isGetDiningOptionsError)
      showToast("error", "Dining option", "Unable to load dining options");
  }, [isGetDiningOptionsError]);

  useEffect(() => {
    if (isGetStationsError)
      showToast("error", "Station", "Unable to load stations");
  }, [isGetStationsError]);

  useEffect(() => {
    if (isUpdateDiningOptionError) {
      showToast(
        "error",
        updateDiningOptionError?.status,
        updateDiningOptionError?.data?.message ||
          (updateDiningOptionError?.data?.errors && "Incorrect data"),
      );
    }
  }, [isUpdateDiningOptionError]);

  useEffect(() => {
    if (isSaveDiningOptionOrderError)
      showToast(
        "error",
        saveDiningOptionOrderError?.status,
        saveDiningOptionOrderError?.data?.message ||
          (saveDiningOptionOrderError?.data?.errors && "Incorrect data"),
      );
  }, [isSaveDiningOptionOrderError]);

  useEffect(() => {
    if (isDeleteDiningOptionSuccess) {
      setShowDeleteModal(false);
      dispatch(
        foodStationApi.util.invalidateTags([
          { type: "FoodStations", id: "LIST" },
        ]),
      );
    }
  }, [isDeleteDiningOptionSuccess]);

  useEffect(() => {
    if (isDeleteDiningOptionError)
      showToast(
        "error",
        deleteDiningOptionError?.status,
        deleteDiningOptionError?.data?.message,
      );
  }, [isDeleteDiningOptionError]);

  const openDeleteModal = (rowData) => () => {
    setId(rowData.id);
    setName(rowData.name);
    setShowDeleteModal(true);
  };

  const onDelete = () => {
    deleteDiningOption({
      id: id,
      stationId: selectedStation.id,
      subdomain: getSubdomain(),
    });
  };

  const handleArrowUpClick = (item) => {
    const sortedOptions = [...options].sort((a, b) => a.order - b.order);

    const index = sortedOptions.findIndex((opt) => opt.id === item.id);

    if (index > 0) {
      [sortedOptions[index - 1], sortedOptions[index]] = [
        sortedOptions[index],
        sortedOptions[index - 1],
      ];
    }

    const newList = sortedOptions.map((opt, idx) => ({
      id: opt.id,
      order: idx + 1,
    }));

    saveDiningOptionOrder({
      payload: newList,
      subDomain: getSubdomain(),
      stationId: selectedStation.id,
    });
  };

  const handleArrowDownClick = (item) => {
    const sortedOptions = [...options].sort((a, b) => a.order - b.order);

    const index = sortedOptions.findIndex((opt) => opt.id === item.id);

    if (index === -1 || index === sortedOptions.length - 1) {
      return;
    }

    [sortedOptions[index], sortedOptions[index + 1]] = [
      sortedOptions[index + 1],
      sortedOptions[index],
    ];

    const newList = sortedOptions.map((opt, idx) => ({
      id: opt.id,
      order: idx + 1,
    }));

    saveDiningOptionOrder({
      payload: newList,
      subDomain: getSubdomain(),
      stationId: selectedStation.id,
    });
  };

  const itemTemplate = (item) => {
    return (
      <div className={styles.diningOptionItem}>
        <div className={styles.column1}>
          <i className="pi pi-align-justify"></i>
        </div>
        <div style={{ width: "100%" }}>{item.name}</div>
        <div
          className={styles.tableColumnOrder}
          style={{
            display: "flex",
            justifyContent: "center",
            flexShrink: 0,
            width: "160px",
            gap: "2px",
          }}
        >
          <Button
            icon="pi pi-arrow-up"
            className={styles.arrowBtn}
            disabled={item.order === 1}
            onMouseDown={() => handleArrowUpClick(item)}
          />
          <Button
            icon="pi pi-arrow-down"
            className={styles.arrowBtn}
            disabled={item.order === options[options.length - 1].order}
            onMouseDown={() => handleArrowDownClick(item)}
          />
        </div>
        <div
          className={styles.tableColumnActive}
          style={{
            display: "flex",
            justifyContent: "center",
            flexShrink: 0,
            width: "260px",
          }}
        >
          {item.active ? (
            <Button
              icon="pi pi-eye"
              className={`p-button-text ${styles.activeBtn}`}
              onMouseUp={() => {
                updateDiningOption({
                  id: item.id,
                  stationId: selectedStation.id,
                  subdomain: getSubdomain(),
                  data: { active: false },
                });
              }}
            />
          ) : (
            <Button
              icon="pi pi-eye-slash"
              className={`p-button-text ${styles.activeBtn}`}
              onMouseUp={() => {
                updateDiningOption({
                  id: item.id,
                  stationId: selectedStation.id,
                  subdomain: getSubdomain(),
                  data: { active: true },
                });
              }}
            />
          )}
        </div>
        <div
          className={styles.tableColumnActions}
          style={{ width: "207px", textAlign: "center", flexShrink: 0 }}
        >
          <div className={styles.cellSoundWrapper}>
            <Button
              icon="pi pi-pencil"
              className={`p-button-text ${styles.soundBtn}`}
              onMouseDown={() => goEdit(item)}
            />
            <Button
              icon="pi pi-trash"
              className={`p-button-text ${styles.deleteBtn}`}
              onMouseDown={openDeleteModal(item)}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.pageHeader}>
        <h2>Dining Options</h2>
        <Dropdown
          value={selectedStation}
          onChange={(e) => setSelectedStation(e.value)}
          options={stations?.toSorted((a, b) => a.name.localeCompare(b.name))}
          optionLabel="name"
          placeholder="Station"
          className={`${styles.dropdown}`}
          itemTemplate={(option) => {
            const subCount = option.sub_stations?.length ?? 0;
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {!!option.is_multi && subCount > 1 && (
                  <span
                    title={`${subCount} sub-stations`}
                    className={styles.multiBadge}
                  >
                    x{subCount}
                  </span>
                )}
                {option.name}
              </div>
            );
          }}
          valueTemplate={(option) => {
            if (!option) return <span>Station</span>;
            const subCount = option.sub_stations?.length ?? 0;
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {!!option.is_multi && subCount > 1 && (
                  <span
                    title={`${subCount} sub-stations`}
                    className={styles.multiBadge}
                  >
                    x{subCount}
                  </span>
                )}
                {option.name}
              </div>
            );
          }}
        />
      </div>
      {isGetStationsSuccess && (
        <div style={{ marginBottom: "10px" }}>
          {selectedStation && (
            <Button className={styles.createBtn} onClick={goCreate}>
              <i className="pi pi-plus"></i> <span>ADD DINING OPTION</span>
            </Button>
          )}
        </div>
      )}

      {selectedStation && isGetDiningOptionSuccess ? (
        <>
          {options?.length ? (
            <>
              {isMobile ? (
                <>
                  <div className={styles.actionsText}>
                    Reorder options using arrows
                  </div>
                  <div className={styles.cardList}>
                    {options.map((option) => (
                      <div key={option.id} className={styles.cardItem}>
                        <div className={styles.cardInfo}>
                          <div className={styles.name}>{option.name}</div>
                        </div>
                        <div className={styles.actions}>
                          <button
                            className={styles.cardArrowBtn}
                            disabled={option.order === 1}
                            onClick={() => handleArrowUpClick(option)}
                          >
                            <i className="pi pi-arrow-up" />
                          </button>
                          <button
                            className={styles.cardArrowBtn}
                            disabled={
                              option.order === options[options.length - 1].order
                            }
                            onClick={() => handleArrowDownClick(option)}
                          >
                            <i className="pi pi-arrow-down" />
                          </button>
                        </div>
                        <div className={styles.actions}>
                          {option.active ? (
                            <button
                              className={styles.cardEditBtn}
                              onClick={() => {
                                updateDiningOption({
                                  id: option.id,
                                  stationId: selectedStation.id,
                                  subdomain: getSubdomain(),
                                  data: { active: false },
                                });
                              }}
                            >
                              <i className="pi pi-eye" />
                            </button>
                          ) : (
                            <button
                              className={styles.cardEditBtn}
                              onClick={() => {
                                updateDiningOption({
                                  id: option.id,
                                  stationId: selectedStation.id,
                                  subdomain: getSubdomain(),
                                  data: { active: true },
                                });
                              }}
                            >
                              <i className="pi pi-eye-slash" />
                            </button>
                          )}
                          <button
                            className={styles.cardEditBtn}
                            onClick={() => goEdit(option)}
                          >
                            <i className="pi pi-pencil" />
                          </button>
                          <button
                            className={styles.cardDeleteBtn}
                            onClick={openDeleteModal(option)}
                          >
                            <i className="pi pi-trash" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div>Drag options to reorder</div>
                  <OrderList
                    dataKey="id"
                    value={options}
                    onChange={(e) => {
                      const newList = e.value.map((item, index) => {
                        return { id: item.id, order: index + 1 };
                      });

                      console.log(newList);
                      saveDiningOptionOrder({
                        payload: newList,
                        subDomain: getSubdomain(),
                        stationId: selectedStation.id,
                      });
                    }}
                    header={
                      <div className={styles.header}>
                        <div className={styles.column1}></div>
                        <div style={{ width: "100%", minWidth: "150px" }}>
                          Title
                        </div>
                        <div
                          className={styles.columnOrder}
                          style={{
                            width: "160px",
                            textAlign: "center",
                            flexShrink: 0,
                          }}
                        >
                          Order
                        </div>
                        <div
                          className={styles.columnActive}
                          style={{
                            width: "260px",
                            textAlign: "center",
                            flexShrink: 0,
                          }}
                        >
                          Active
                        </div>
                        <div
                          className={styles.columnActions}
                          style={{
                            width: "191px",
                            textAlign: "center",
                            flexShrink: 0,
                          }}
                        >
                          Actions
                        </div>
                      </div>
                    }
                    itemTemplate={itemTemplate}
                    dragdrop={!isSaveDiningOptionOrderLoading}
                    className={styles.orderListItem}
                  ></OrderList>
                </>
              )}
            </>
          ) : (
            <div>This Station don't have any dining options</div>
          )}
        </>
      ) : (
        <div>Please, select station</div>
      )}

      {/* Delete modal */}
      <Dialog
        closable={false}
        blockScroll
        header={
          <div style={{ width: "100%", textAlign: "center" }}>
            Are You Sure?
          </div>
        }
        visible={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
        }}
        footer={() => (
          <div className="flex flex-column gap-2 justify-content-end">
            <Button
              label="Delete"
              className={`p-button-success ${styles.deleteDialogBtn}`}
              onClick={onDelete}
            />
            <Button
              label="Cancel"
              text
              className={`p-button-success ${styles.cancelDialogBtn}`}
              onClick={() => {
                setShowDeleteModal(false);
              }}
            />
          </div>
        )}
        className={`p-fluid ${styles.deleteModal}`}
      >
        <div className={styles.text}>
          <span>
            You want to delete the dining option <strong>{name}</strong>.
          </span>
          Confirm this action by pressing ‘Delete’ or cancel it.
        </div>
      </Dialog>
    </div>
  );
};

export default DiningOptions;
